import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, HeartPulse, Focus, ArrowRight, ArrowLeft, CheckCircle, AlertTriangle, Info, Calendar, Shield } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

/* ── Validated Screening Instruments ── */

const PHQ9_QUESTIONS = [
  'Little interest or pleasure in doing things',
  'Feeling down, depressed, or hopeless',
  'Trouble falling or staying asleep, or sleeping too much',
  'Feeling tired or having little energy',
  'Poor appetite or overeating',
  'Feeling bad about yourself — or that you are a failure or have let yourself or your family down',
  'Trouble concentrating on things, such as reading the newspaper or watching television',
  'Moving or speaking so slowly that other people could have noticed? Or the opposite — being so fidgety or restless that you have been moving around a lot more than usual',
  'Thoughts that you would be better off dead, or of hurting yourself in some way'
];

const GAD7_QUESTIONS = [
  'Feeling nervous, anxious, or on edge',
  'Not being able to stop or control worrying',
  'Worrying too much about different things',
  'Trouble relaxing',
  'Being so restless that it is hard to sit still',
  'Becoming easily annoyed or irritable',
  'Feeling afraid, as if something awful might happen'
];

const ASRS_QUESTIONS = [
  'How often do you have trouble wrapping up the final details of a project, once the challenging parts have been done?',
  'How often do you have difficulty getting things in order when you have to do a task that requires organization?',
  'How often do you have problems remembering appointments or obligations?',
  'When you have a task that requires a lot of thought, how often do you avoid or delay getting started?',
  'How often do you fidget or squirm with your hands or feet when you have to sit down for a long time?',
  'How often do you feel overly active and compelled to do things, like you were driven by a motor?'
];

const FREQUENCY_OPTIONS = [
  { value: 0, label: 'Not at all' },
  { value: 1, label: 'Several days' },
  { value: 2, label: 'More than half the days' },
  { value: 3, label: 'Nearly every day' }
];

const ASRS_OPTIONS = [
  { value: 0, label: 'Never' },
  { value: 1, label: 'Rarely' },
  { value: 2, label: 'Sometimes' },
  { value: 3, label: 'Often' },
  { value: 4, label: 'Very Often' }
];

/* ── Scoring & Interpretation ── */

const interpretPHQ9 = (score) => {
  if (score <= 4) return { level: 'Minimal', color: 'emerald', severity: 'minimal', message: 'Your responses suggest minimal depressive symptoms. Continue healthy habits like regular exercise, sleep, and social connections.', recommend: false };
  if (score <= 9) return { level: 'Mild', color: 'yellow', severity: 'mild', message: 'Your responses suggest mild depressive symptoms. These symptoms may benefit from monitoring and self-care strategies. If they persist or worsen, consider speaking with a mental health professional.', recommend: true };
  if (score <= 14) return { level: 'Moderate', color: 'orange', severity: 'moderate', message: 'Your responses suggest moderate depressive symptoms. A professional evaluation could help determine if treatment approaches like CBT (Cognitive Behavioral Therapy) might benefit you.', recommend: true };
  if (score <= 19) return { level: 'Moderately Severe', color: 'red', severity: 'moderately_severe', message: 'Your responses suggest moderately severe depressive symptoms. We strongly recommend scheduling a professional evaluation to discuss treatment options.', recommend: true };
  return { level: 'Severe', color: 'red', severity: 'severe', message: 'Your responses suggest severe depressive symptoms. Please seek a professional evaluation as soon as possible. If you are in crisis, call 988 (Suicide & Crisis Lifeline) or go to your nearest emergency room.', recommend: true };
};

const interpretGAD7 = (score) => {
  if (score <= 4) return { level: 'Minimal', color: 'emerald', severity: 'minimal', message: 'Your responses suggest minimal anxiety symptoms. Continue practicing healthy coping strategies like mindfulness, exercise, and adequate rest.', recommend: false };
  if (score <= 9) return { level: 'Mild', color: 'yellow', severity: 'mild', message: 'Your responses suggest mild anxiety symptoms. Monitoring and self-care strategies may help. If symptoms persist, a mental health professional can provide guidance.', recommend: true };
  if (score <= 14) return { level: 'Moderate', color: 'orange', severity: 'moderate', message: 'Your responses suggest moderate anxiety symptoms. Approaches like CBT can help you recognize connections between anxious thoughts, feelings, and behaviors. A professional evaluation is recommended.', recommend: true };
  return { level: 'Severe', color: 'red', severity: 'severe', message: 'Your responses suggest severe anxiety symptoms. We strongly recommend scheduling a professional evaluation to explore evidence-based treatment options.', recommend: true };
};

const interpretASRS = (score) => {
  // ASRS Part A: 4+ items scored in the shaded area indicates further evaluation needed
  // Simplified: total score thresholds
  if (score <= 6) return { level: 'Minimal Indicators', color: 'emerald', severity: 'minimal', message: 'Your responses suggest minimal indicators of attention-related difficulties. These results are within a typical range.', recommend: false };
  if (score <= 13) return { level: 'Mild Indicators', color: 'yellow', severity: 'mild', message: 'Your responses suggest some attention-related difficulties that may warrant further exploration. A professional evaluation could help clarify whether these patterns reflect ADHD or other factors.', recommend: true };
  if (score <= 19) return { level: 'Moderate Indicators', color: 'orange', severity: 'moderate', message: 'Your responses suggest moderate indicators consistent with attention difficulties. A comprehensive professional evaluation is recommended to determine if ADHD or other conditions may be contributing.', recommend: true };
  return { level: 'Significant Indicators', color: 'red', severity: 'significant', message: 'Your responses suggest significant indicators of attention-related difficulties. We recommend scheduling a professional evaluation as soon as possible.', recommend: true };
};

/* ── Assessment Card ── */

const AssessmentCard = ({ icon: Icon, title, desc, color, duration, onClick, testId }) => (
  <button onClick={onClick} className={`text-left p-6 rounded-2xl border-2 hover:shadow-lg transition-all duration-300 group ${color}`} data-testid={testId}>
    <div className="flex items-start gap-4">
      <div className="p-3 rounded-xl bg-white/80 group-hover:scale-110 transition-transform"><Icon className="w-7 h-7" /></div>
      <div className="flex-1">
        <h3 className="text-lg font-bold text-gray-900 mb-1">{title}</h3>
        <p className="text-sm text-gray-600 mb-3">{desc}</p>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>{duration}</span>
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </div>
  </button>
);

/* ── Main Page ── */

const MentalHealthAssessmentPage = () => {
  const navigate = useNavigate();
  const [activeAssessment, setActiveAssessment] = useState(null); // 'depression' | 'anxiety' | 'adhd'
  const [answers, setAnswers] = useState({});
  const [currentQ, setCurrentQ] = useState(0);
  const [showResult, setShowResult] = useState(false);

  const assessments = {
    depression: { title: 'Depression Screening', subtitle: 'PHQ-9', questions: PHQ9_QUESTIONS, options: FREQUENCY_OPTIONS, interpret: interpretPHQ9, prompt: 'Over the last 2 weeks, how often have you been bothered by the following?' },
    anxiety: { title: 'Anxiety Screening', subtitle: 'GAD-7', questions: GAD7_QUESTIONS, options: FREQUENCY_OPTIONS, interpret: interpretGAD7, prompt: 'Over the last 2 weeks, how often have you been bothered by the following?' },
    adhd: { title: 'ADHD Screening', subtitle: 'ASRS-v1.1 Part A', questions: ASRS_QUESTIONS, options: ASRS_OPTIONS, interpret: interpretASRS, prompt: 'Please rate how often the following applies to you:' }
  };

  const startAssessment = (type) => {
    setActiveAssessment(type);
    setAnswers({});
    setCurrentQ(0);
    setShowResult(false);
  };

  const reset = () => {
    setActiveAssessment(null);
    setAnswers({});
    setCurrentQ(0);
    setShowResult(false);
  };

  const currentAssessment = activeAssessment ? assessments[activeAssessment] : null;
  const totalQ = currentAssessment?.questions?.length || 0;
  const score = Object.values(answers).reduce((sum, v) => sum + v, 0);
  const result = currentAssessment && showResult ? currentAssessment.interpret(score) : null;
  const allAnswered = totalQ > 0 && Object.keys(answers).length === totalQ;

  /* ── Selection Screen ── */
  if (!activeAssessment) {
    return (
      <>
        <SEO title="Mental Health Self-Assessment" description="Take a free, confidential mental health self-assessment (PHQ-9, GAD-7, ASRS) to screen for depression, anxiety, or ADHD symptoms in about 3 minutes, and get guidance on next steps from SDORA Wellbeing." path="/self-assessment" image="https://static.prod-images.emergentagent.com/jobs/01501ca6-ddde-43b6-bfc5-0444b2f0bae0/images/ad7041efc75e494a71de5f4e7ebeacf6d4e552b40bdd2393c934ffe639610f83.jpeg" />
        <Navigation />
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-4xl mx-auto px-4 py-16">
            <div className="text-center mb-12">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Mental Health Self-Assessment</h1>
              <p className="text-base text-gray-600 max-w-2xl mx-auto">Choose an assessment below to learn more about your mental health. Each takes approximately 3 minutes to complete.</p>
            </div>

            {/* Disclaimer */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8 flex gap-3" data-testid="pre-disclaimer">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-800">These self-assessments are screening tools designed to help you reflect on your experiences. They are <strong>not diagnostic tools</strong> and cannot replace a professional evaluation. Only a qualified clinician can provide a proper diagnosis and treatment plan.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <AssessmentCard
                icon={HeartPulse} title="Depression" desc="Screens for symptoms like persistent sadness, loss of interest, changes in sleep or appetite, and low energy that may affect daily life."
                color="border-violet-200 bg-violet-50/50 hover:border-violet-400" duration="~3 min | 9 questions (PHQ-9)" onClick={() => startAssessment('depression')} testId="start-depression"
              />
              <AssessmentCard
                icon={Brain} title="Anxiety" desc="Screens for symptoms like excessive worry, restlessness, difficulty relaxing, and feeling on edge that may interfere with daily functioning."
                color="border-sky-200 bg-sky-50/50 hover:border-sky-400" duration="~2 min | 7 questions (GAD-7)" onClick={() => startAssessment('anxiety')} testId="start-anxiety"
              />
              <AssessmentCard
                icon={Focus} title="ADHD" desc="Screens for attention-related patterns like difficulty organizing, forgetfulness, restlessness, and trouble completing tasks."
                color="border-amber-200 bg-amber-50/50 hover:border-amber-400" duration="~2 min | 6 questions (ASRS)" onClick={() => startAssessment('adhd')} testId="start-adhd"
              />
            </div>

            <div className="text-center mt-10">
              <p className="text-xs text-gray-400">Your responses are completely private and are not stored or shared.</p>
            </div>

            <div className="mt-12 max-w-2xl mx-auto text-center" data-testid="self-assessment-why-it-matters">
              <h2 className="text-lg font-bold text-gray-900 mb-2">Why Take a Self-Assessment?</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                Recognizing the early signs of depression, anxiety, or ADHD can be the first step toward feeling
                better. These validated screening questionnaires are used by clinicians nationwide to spot patterns
                in mood, focus, and daily functioning. If your results suggest you could benefit from support, our
                team at SDORA Wellbeing can schedule a full comprehensive assessment and build a personalized
                treatment plan with you, in person or by telehealth.
              </p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  /* ── Results Screen ── */
  if (showResult && result) {
    const colorMap = { emerald: 'bg-emerald-50 border-emerald-300 text-emerald-800', yellow: 'bg-yellow-50 border-yellow-300 text-yellow-800', orange: 'bg-orange-50 border-orange-300 text-orange-800', red: 'bg-red-50 border-red-300 text-red-800' };
    const badgeMap = { emerald: 'bg-emerald-100 text-emerald-700', yellow: 'bg-yellow-100 text-yellow-700', orange: 'bg-orange-100 text-orange-700', red: 'bg-red-100 text-red-700' };

    return (
      <>
        <SEO title={`${currentAssessment.title} Results`} />
        <Navigation />
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-2xl mx-auto px-4 py-16">
            <div className="text-center mb-8">
              <CheckCircle className={`w-16 h-16 mx-auto mb-4 ${result.color === 'emerald' ? 'text-emerald-500' : result.color === 'yellow' ? 'text-yellow-500' : result.color === 'orange' ? 'text-orange-500' : 'text-red-500'}`} />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Assessment Complete</h1>
              <p className="text-sm text-gray-500">{currentAssessment.title} ({currentAssessment.subtitle})</p>
            </div>

            {/* Score Card */}
            <div className={`rounded-2xl border-2 p-6 mb-6 ${colorMap[result.color]}`} data-testid="result-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">{result.level}</h2>
                <span className={`text-sm font-bold px-3 py-1 rounded-full ${badgeMap[result.color]}`}>Score: {score}/{totalQ * (activeAssessment === 'adhd' ? 4 : 3)}</span>
              </div>
              <p className="text-sm leading-relaxed">{result.message}</p>
            </div>

            {/* CBT Info */}
            {result.recommend && (
              <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
                <h3 className="text-sm font-bold text-gray-800 mb-2">How CBT Can Help</h3>
                <p className="text-sm text-gray-600">Cognitive Behavioral Therapy (CBT) helps people recognize connections between their thoughts, feelings, and behaviors. By identifying unhelpful thinking patterns and developing healthier coping strategies, many people experience meaningful improvement in their symptoms. A qualified mental health professional can determine if CBT or other approaches are right for you.</p>
              </div>
            )}

            {/* Appointment CTA */}
            {result.recommend && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6 text-center">
                <p className="text-sm text-blue-800 mb-3">Based on your responses, speaking with a mental health professional may be beneficial.</p>
                <button onClick={() => navigate('/appointments')} className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700 transition-colors" data-testid="make-appointment-btn">
                  <Calendar className="w-5 h-5" /> Make an Appointment
                </button>
              </div>
            )}

            {/* Required Disclaimer */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex gap-3" data-testid="result-disclaimer">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800 mb-1">Important Notice</p>
                <p className="text-sm text-amber-700">This self-assessment is not a diagnostic tool. It is designed to help you understand if you might benefit from speaking with a mental health professional. Only a qualified clinician can provide a proper diagnosis and treatment plan.</p>
              </div>
            </div>

            {/* Crisis Resources */}
            {(result.severity === 'severe' || result.severity === 'moderately_severe') && (
              <div className="bg-red-50 border border-red-300 rounded-xl p-4 mb-6">
                <p className="text-sm font-bold text-red-800 mb-1">If you are in crisis:</p>
                <p className="text-sm text-red-700">Call <strong>988</strong> (Suicide & Crisis Lifeline) | Text "HELLO" to <strong>741741</strong> | Call <strong>911</strong> for emergencies</p>
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <button onClick={reset} className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-50" data-testid="take-another">Take Another Assessment</button>
            </div>

            <div className="text-center mt-6">
              <p className="text-xs text-gray-400 flex items-center justify-center gap-1"><Shield className="w-3 h-3" /> Your responses were not stored or shared.</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  /* ── Question Screen ── */
  const q = currentAssessment.questions[currentQ];
  const progress = ((currentQ + 1) / totalQ) * 100;

  return (
    <>
      <SEO title={currentAssessment.title} />
      <Navigation />
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-2xl mx-auto px-4 py-12">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-xl font-bold text-gray-900">{currentAssessment.title}</h1>
              <p className="text-xs text-gray-500">{currentAssessment.subtitle}</p>
            </div>
            <button onClick={reset} className="text-sm text-gray-400 hover:text-gray-600">Exit</button>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
            <div className="bg-blue-500 h-2 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-xs text-gray-500 mb-6">Question {currentQ + 1} of {totalQ}</p>

          {/* Prompt */}
          <p className="text-sm text-gray-500 mb-4 italic">{currentAssessment.prompt}</p>

          {/* Question */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6" data-testid={`question-${currentQ}`}>
            <p className="text-base font-semibold text-gray-800 mb-5">{q}</p>
            <div className="space-y-2">
              {currentAssessment.options.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setAnswers({ ...answers, [currentQ]: opt.value })}
                  className={`w-full text-left px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                    answers[currentQ] === opt.value
                      ? 'border-blue-500 bg-blue-50 text-blue-800'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                  data-testid={`option-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <button
              onClick={() => setCurrentQ(Math.max(0, currentQ - 1))}
              disabled={currentQ === 0}
              className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-30"
              data-testid="prev-question"
            >
              <ArrowLeft className="w-4 h-4" /> Previous
            </button>
            {currentQ < totalQ - 1 ? (
              <button
                onClick={() => setCurrentQ(currentQ + 1)}
                disabled={answers[currentQ] === undefined}
                className="flex items-center gap-1 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="next-question"
              >
                Next <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={() => setShowResult(true)}
                disabled={!allAnswered}
                className="flex items-center gap-1 px-5 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-bold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="view-results"
              >
                <CheckCircle className="w-4 h-4" /> View Results
              </button>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default MentalHealthAssessmentPage;
